import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { Classroom as ClassroomConstants } from '@constants'
import { assertContext as assert, isClient } from '@helpers'
import { useClassroomQuery } from '../hooks'
import { useAuth } from '../../../hooks'
import { useClassroomEnv } from '../../ClassroomEnv'

const classroomPermissionsContext = createContext()

// This should really work based on platform stuff and maybe even come from DB but this hook should be a good start
// In case certain env/apptType/ptype combinations are not defined, the default value is ALLOWED
const ACL = {
  [ClassroomConstants.ENV.RECORDING]: {
    group: {
      manager: {
        [ClassroomConstants.FEATURES.TOOL_PANEL_TOGGLE]: false,
        [ClassroomConstants.FEATURES.TOOL_SWITCH_FILE]: false,
        [ClassroomConstants.FEATURES.TOOL_NEW_FILE]: false
      },
      client: {
        [ClassroomConstants.FEATURES.TOOL_PANEL_TOGGLE]: false,
        [ClassroomConstants.FEATURES.TOOL_SWITCH_FILE]: false,
        [ClassroomConstants.FEATURES.TOOL_NEW_FILE]: false
      }
    }
  },
  [ClassroomConstants.ENV.CLASSROOM]: {
    group: {
      client: {
        [ClassroomConstants.FEATURES.TOOL_PANEL_TOGGLE]: false,
        [ClassroomConstants.FEATURES.TOOL_NEW_FILE]: false,
        [ClassroomConstants.FEATURES.TOOL_SWITCH_FILE]: false,
        [ClassroomConstants.FEATURES.TOOL_RENAME_FILE]: false,
        [ClassroomConstants.FEATURES.TOOL_EDIT]: true,
        [ClassroomConstants.FEATURES.SCREEN_SHARE_TOGGLE]: false,
        [ClassroomConstants.FEATURES.SKETCH_ERASER]: false,
        [ClassroomConstants.FEATURES.SKETCH_UNDO_REDO]: false,
        [ClassroomConstants.FEATURES.VIDEO_UPLOAD_CUSTOM_BACKGROUNDS]: false,
        [ClassroomConstants.FEATURES.FULL_SCREEN_TOGGLE]: false
      }
    }
  }
}

export const ClassroomPermissionsProvider = ({ children }) => {
  const { env } = useClassroomEnv()
  const {
    data: { type: apptType }
  } = useClassroomQuery()
  const {
    profile: { type: ptype }
  } = useAuth()

  const checkPermission = useCallback(
    (feature, _env = env) => {
      const envLevel = ACL[_env]
      if (!envLevel) return true

      const apptTypeLevel = envLevel[apptType]
      if (!apptTypeLevel) return true

      const featureListLevel =
        apptTypeLevel[isClient(ptype) ? 'client' : 'manager']
      if (!featureListLevel) return true

      return featureListLevel[feature] ?? true
    },
    [apptType, ptype, env]
  )

  const value = useMemo(() => {
    return {
      checkPermission
    }
  }, [checkPermission])

  return (
    <classroomPermissionsContext.Provider value={value}>
      {children}
    </classroomPermissionsContext.Provider>
  )
}

export const useClassroomPermissions = () => {
  return assert(
    useContext(classroomPermissionsContext),
    'useClassroomPermissions',
    'ClassroomPermissionsProvider'
  )
}
